import { Box, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { DateTime, Duration } from 'luxon';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';
import Alert from '../../../../../components/Alert/Alert';
import Button from '../../../../../components/Button/Button';
import ButtonGroup from '../../../../../components/ButtonGroup/ButtonGroup';
import Checkbox from '../../../../../components/Checkbox/Checkbox';
import DateCalendar from '../../../../../components/DateCalendar/DateCalendar';
import { SimStatus } from '../../../../../models/UserDevicesModels';
import { RootState } from '../../../../../stores/Store';
import { updateRequest } from '../../../../../stores/TerminateConnectionStore';

interface TerminateConnectionDateSelectionStepProps {
  nextStep: () => void;
  onClose: () => void;
}

function TerminateConnectionDateSelectionStep({ nextStep, onClose }: TerminateConnectionDateSelectionStepProps) {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const { request } = useSelector((state: RootState) => state.terminateConnectionReducer);
  const { simStatus } = useSelector((state: RootState) => state.upgradeDeviceReducer);
  const { companyConfigs } = useSelector((state: RootState) => state.userDetailsReducer);
  const today = DateTime.now();
  const maxTerminationDate = companyConfigs?.scheduleEndDateWindowLimit
    ? today.plus(Duration.fromISO(companyConfigs.scheduleEndDateWindowLimit)).startOf('day')
    : today.plus({ year: 1 }).startOf('day');

  const formik = useFormik({
    initialValues: { terminationDate: request.terminationDate, suspendImmediately: request.suspendImmediately },
    enableReinitialize: false,
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: yup.object({
      terminationDate: yup.string().nullable(),
    }),
    onSubmit: (values) => {
      dispatch(
        updateRequest({
          terminationDate: values.terminationDate as string,
          suspendImmediately: values.suspendImmediately,
        }),
      );
      nextStep();
    },
  });

  return (
    <form onSubmit={formik.handleSubmit} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ marginBottom: '2rem' }}>
        <Typography sx={{ marginBottom: '2.5rem' }}>
          {t('pages.userDetails.terminateConnectionDrawer.chooseTerminationDate')}
        </Typography>
        <Box sx={{ input: { width: '13.375rem' } }}>
          <DateCalendar
            onChange={(date) => {
              formik.setFieldValue('terminationDate', date?.toISO());
            }}
            error={Boolean(formik.errors.terminationDate)}
            label={t('pages.userDetails.terminateConnectionDrawer.scheduleTermination')}
            feedback={formik.errors.terminationDate ? formik.errors.terminationDate : ''}
            value={formik.values.terminationDate ? DateTime.fromISO(formik.values.terminationDate) : null}
            minDate={today.plus({ day: 31 }).startOf('day')}
            maxDate={maxTerminationDate}
          />
        </Box>
      </Box>

      <Alert
        alertType="info"
        message={
          <>
            <Trans>{t('pages.userDetails.terminateConnectionDrawer.scheduleTerminationWarning')}</Trans>
            {simStatus !== SimStatus.SUSPENDED && (
              <Box sx={{ paddingTop: '1rem' }}>{t('pages.userDetails.terminateConnectionDrawer.immediateNote')}</Box>
            )}
          </>
        }
        fontSize="0.875rem"
      />
      {simStatus !== SimStatus.SUSPENDED ? (
        <Box sx={{ marginTop: '2.5rem' }}>
          <Checkbox
            checked={formik.values.suspendImmediately}
            onChange={(e) => {
              formik.setFieldValue('suspendImmediately', e.target.checked);
            }}
            label={t('pages.userDetails.terminateConnectionDrawer.suspendImmediately')}
          />
        </Box>
      ) : null}

      <Box sx={{ marginTop: 'auto' }}>
        <ButtonGroup>
          <Button label={t('common.cancel')} color="alternative2Button" onClick={onClose} />
          <Button
            label={t('common.continue')}
            color="primaryButton"
            disabled={!formik.isValid}
            onClick={formik.submitForm}
          />
        </ButtonGroup>
      </Box>
    </form>
  );
}

export default TerminateConnectionDateSelectionStep;
